import { CellContext, ColumnDef } from '@tanstack/react-table';
import { AppTableFeatures } from '~/components/table/appTableFeatures';
import { camelCase } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StockOnHand } from '~/types';
import { Table } from '../../table/Table';

type Props = {
  data: StockOnHand[];
  containerRef: React.RefObject<HTMLDivElement>;
  isLoading?: boolean;
  [key: string]: any;
};

export const StockOnHandTable = ({
  data,
  containerRef,
  isLoading = false,
  ...rest
}: Props) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<AppTableFeatures, StockOnHand>[]>(() => {
    return [
      {
        header: t('sku') ?? 'SKU',
        accessorKey: 'skuName',
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) => (
          <>
            <div className='font-semibold text-primary'>
              {info.getValue<string>()}
            </div>
            {/*  {info.row.original.skuCode && (
                <div className='text-secondary'>{info.row.original.skuCode}</div>
              )} */}
          </>
        ),
        size: 300,
        enableResizing: true,
      },
      /* {
        header: t('itemTypeLabel'),
        accessorKey: 'itemType',
        size: 80,
        cell: (info) => t(camelCase(info.getValue<string>() ?? '')),
      }, */
      {
        header: 'Location Type',
        accessorKey: 'locationType',
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) =>
          t(camelCase(info.getValue<string>() ?? '')),
        size: 150,
      },
      {
        header: 'Location',
        accessorKey: 'locationName',
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) =>
          info.getValue(),
        size: 150,
      },
      {
        header: 'Space',
        accessorKey: 'spaceName',
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) =>
          info.getValue(),
        size: 100,
      },
      {
        header: 'Total on Hand',
        accessorKey: 'soh',
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) =>
          info.getValue(),
        size: 115,
        meta: {
          align: 'right',
        },
      },
      {
        header: 'Reserved on Hand',
        accessorKey: 'reserved',
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) =>
          info.getValue(),
        size: 120,
        meta: {
          align: 'right',
        },
      },
      {
        header: 'Available on Hand',
        id: 'availableSoh',
        accessorFn: (row) => row.soh - row.reserved,
        cell: (info: CellContext<AppTableFeatures, StockOnHand, unknown>) =>
          info.getValue(),
        size: 120,
        meta: {
          align: 'right',
        },
      },
    ];
  }, [t]);

  const initialSortBy = useMemo(
    () => [
      { id: 'skuName', desc: true }, // Sort by SKU Name descending by default
    ],
    []
  );

  return (
    <Table<StockOnHand>
      columns={columns}
      data={data}
      containerRef={containerRef}
      initialSortBy={initialSortBy}
      isLoading={isLoading}
      {...rest}
    />
  );
};
